// U5 "Dictate a reply" (experiment): a mic button that listens with Chrome's speech recognition,
// shows the live transcript, and on stop hands the words over (the card sends them as the owner's
// draft instruction; the Dock puts them in its input). Shown only when the owner turned dictation
// on in Settings and the browser is Chrome: Safari's recognition sends audio to Apple, which the
// privacy rule doesn't allow. The audio goes to Google's speech service, never to MeeMail.
import { useEffect, useMemo, useRef, useState } from "react";
import { Mic, Square, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@web/lib/cn";
import {
  browserSpeechSupport,
  dictationErrorWords,
  listen,
  type Listener,
} from "@web/lib/dictation";
import { useDictationSetting } from "@web/lib/unstickApi";

/** Dictation is on in Settings and this browser is Chrome with speech recognition. */
export function useDictationAvailable(): boolean {
  const on = useDictationSetting().data?.on ?? false;
  const support = useMemo(() => browserSpeechSupport(), []);
  return on && support === "chrome";
}

export function DictateButton({
  onDone,
  disabled,
  label = "Dictate",
  className,
}: {
  onDone: (words: string) => void;
  disabled?: boolean;
  label?: string;
  className?: string;
}) {
  const available = useDictationAvailable();
  const [listening, setListening] = useState(false);
  const [heard, setHeard] = useState({ final: "", interim: "" });
  const listener = useRef<Listener | null>(null);
  const words = useRef("");
  const keep = useRef(true);
  const done = useRef(onDone);
  useEffect(() => {
    done.current = onDone;
  });
  useEffect(() => () => listener.current?.abort(), []);
  if (!available) return null;

  const start = () => {
    keep.current = true;
    words.current = "";
    setHeard({ final: "", interim: "" });
    listener.current = listen({
      onTranscript: (t) => {
        words.current = [t.final, t.interim].filter(Boolean).join(" ");
        setHeard(t);
      },
      onError: (error) => {
        keep.current = false;
        toast(dictationErrorWords(error));
      },
      onEnd: () => {
        listener.current = null;
        setListening(false);
        const said = words.current.trim();
        if (keep.current && said) done.current(said);
      },
    });
    setListening(!!listener.current);
  };
  const stop = () => listener.current?.stop();
  const cancel = () => {
    keep.current = false;
    listener.current?.abort();
    setListening(false);
  };

  if (!listening)
    return (
      <button
        type="button"
        disabled={disabled}
        onPointerDown={(e) => e.preventDefault()}
        onClick={start}
        title="Dictate (Chrome sends the audio to Google's speech service)"
        data-testid="dictate"
        className={cn(
          "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-sm text-muted hover:bg-sunk hover:text-ink disabled:opacity-60",
          className,
        )}
      >
        <Mic aria-hidden className="size-4" />
        {label}
      </button>
    );

  return (
    <span
      role="status"
      aria-live="polite"
      data-testid="dictating"
      className={cn(
        "inline-flex max-w-full flex-wrap items-center gap-2 rounded-lg border border-line bg-bg px-2.5 py-1 text-sm",
        className,
      )}
    >
      <Mic aria-hidden className="size-4 shrink-0 text-accent" />
      <span className="min-w-0 break-words text-ink">
        {heard.final || heard.interim ? (
          <>
            {heard.final} <span className="text-muted">{heard.interim}</span>
          </>
        ) : (
          <span className="text-muted">Listening…</span>
        )}
      </span>
      <button
        type="button"
        onClick={stop}
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-ink hover:bg-sunk"
      >
        <Square aria-hidden className="size-3.5" />
        Stop
      </button>
      <button
        type="button"
        onClick={cancel}
        aria-label="Cancel dictation"
        className="rounded-md p-0.5 text-muted hover:bg-sunk hover:text-ink"
      >
        <X aria-hidden className="size-3.5" />
      </button>
    </span>
  );
}
